import { Component, Input, Output, EventEmitter } from '@angular/core';
import { QueueEntry, NowShowing, positionOf, waitMs, remainingMs, humanWait } from '../../../models/led-queue.model';

@Component({
  selector: 'app-queue-status',
  templateUrl: './queue-status.component.html',
  styleUrls: ['./queue-status.component.scss'],
  standalone: false,
})
export class QueueStatusComponent {
  /** The caller's own place in the line, if they still have one. */
  @Input() myKey: string | null = null;
  @Input() queue: QueueEntry[] = [];
  @Input() now: NowShowing | null = null;
  /** Ticking clock from the page, so the countdown updates without a timer here. */
  @Input() clock = Date.now();
  @Input() thumbnail: string | null = null;
  /** False for a design sent straight from the gallery — it is already saved. */
  @Input() canSave = true;

  /** True for the board's owner, who may take anything out of the line. */
  @Input() canManage = false;

  @Output() remove = new EventEmitter<string>();
  @Output() clearAll = new EventEmitter<void>();
  @Output() leave = new EventEmitter<void>();
  @Output() again = new EventEmitter<void>();
  @Output() save = new EventEmitter<string>();

  saveName = '';

  /** True while the board is showing the design this screen is about. */
  get mine(): boolean { return !!this.myKey && this.now?.id === this.myKey; }
  get position(): number { return positionOf(this.myKey, this.queue); }
  get waiting(): boolean { return this.position > 0; }

  /** Gone: either it has had its turn, or the line was left. */
  get finished(): boolean { return !this.mine && !this.waiting; }

  get secondsLeft(): number { return Math.ceil(remainingMs(this.now, this.clock) / 1000); }
  get waitText(): string { return humanWait(waitMs(this.myKey, this.queue, this.now, this.clock)); }

  /** 0-100, for the bar under the headline while a design is showing. */
  get progress(): number {
    if (!this.now || !this.now.holdMs) { return 0; }
    const done = this.now.holdMs - remainingMs(this.now, this.clock);
    return Math.max(0, Math.min(100, Math.round(done / this.now.holdMs * 100)));
  }

  get headline(): string {
    if (this.mine) { return "It's on the board — look up"; }
    if (this.position === 1) { return "You're next"; }
    if (this.waiting) { return `You're ${this.position} in line`; }
    return 'Your turn is done';
  }

  get subhead(): string {
    if (this.mine) { return `On for ${this.secondsLeft} more second${this.secondsLeft === 1 ? '' : 's'}.`; }
    if (this.waiting) { return `Up ${this.waitText}. You can put your phone away — the board remembers.`; }
    return 'Thanks for lighting it up. Make another whenever you like.';
  }

  /** The line as shown: what's on now, then everyone waiting. */
  get others(): QueueEntry[] { return this.queue; }

  isMine(q: QueueEntry): boolean { return !!this.myKey && q.key === this.myKey; }

  /** Asking twice, because someone else's turn disappearing is not undoable. */
  confirmingClear = false;
  askClear() { this.confirmingClear = true; }
  cancelClear() { this.confirmingClear = false; }
  doClear() { this.confirmingClear = false; this.clearAll.emit(); }

  removeEntry(q: QueueEntry) {
    if (!this.canManage || !q.key) { return; }
    this.remove.emit(q.key);
  }

  submitSave() {
    const name = this.saveName.trim();
    if (!name) { return; }
    this.save.emit(name);
    this.saveName = '';
  }

  trackByKey(_i: number, q: QueueEntry) { return q.key; }
}
